// [목적] 모바일 하단 탭 네비게이션 — 4탭: 홈/만들기/발견/나
// [이유] W2(design-alignment): 하드코딩 쿨톤 상수(TEXT_HI/#14181F, TEXT_LOW/#8B949E,
//         BORDER/#E1E7EF, BRAND/#1F6FEB)를 paper/ink/line/brand 정본 토큰으로 교체.
//         R-9: 기존 "gangwonfolio.html 1:1 포팅 SoT" 주석을 "정본=paper/ink/line" 으로 갱신.
//         W3-2(design-alignment): "발견" 탭 아이콘을 ◎→⊕ 로 통일 — 시안 shell.jsx NAV 기준(Sidebar 와 동일).
// [사이드 이팩트] viewport.isMobile 일 때 App.tsx 가 렌더. 가운데 강조 없음(원본 디자인 그대로).
// [수정 시 고려사항] 라우트 변경 시 TABS 동기화. 데스크톱 Sidebar 와 NAV 가 다른 점 의도된 것.
//                   pages/auth/** 쿨톤은 의도된 전용 톤(ADR-DA6) — 절대 변경 금지.

import { Box, ButtonBase, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useLocation, useNavigate } from 'react-router-dom';

// [목적] paper/ink/line/brand 정본 토큰 바인딩 — W2(design-alignment) 쿨톤 정본화(R-9)
// [이유] ADR-TR8: 신규 토큰 추가 없이 기존 정본 토큰만 사용. [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//         TEXT_HI: ink.high(warm #29261B) ← 쿨톤 #14181F
//         TEXT_LOW: ink.low(#6F6A5C) ← 쿨톤 #8B949E — 비활성 탭 아이콘 보조색(비본문 한정)
//         BORDER: line.base(#E8E2D5) ← 쿨톤 #E1E7EF
//         BRAND: brand[500] — 동일 값 유지(#1F6FEB). 하드코딩 → 토큰 참조로 전환.
// [사이드 이팩트] 탭 배경 #fff → paper[0] (#FFFFFF, 동일값). 라벨/아이콘 색 온도 warm 전환.
// [수정 시 고려사항] ink.low(#6F6A5C) 는 비활성 탭 아이콘·라벨(보조색) — 본문 미사용(ADR-TR8). [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
const c = tokens.color;
const L = 'light' as const;
const TEXT_HI = c.ink.high[L]; // #29261B (← 하드코딩 #14181F)
const TEXT_LOW = c.ink.low[L]; // #6F6A5C — 비활성 탭 보조색(ADR-TR8, 본문 미사용)
const BORDER = c.line.base[L]; // #E8E2D5 (← 하드코딩 #E1E7EF)
const BRAND = c.brand[500][L]; // #1F6FEB (하드코딩 → 토큰 참조)

interface Tab {
  id: string;
  label: string;
  icon: string;
  to: string;
}

const TABS: ReadonlyArray<Tab> = [
  { id: 'home', label: '홈', icon: '⌂', to: '/' },
  { id: 'chat', label: '만들기', icon: '✦', to: '/chat' },
  // [이유] W3-2: 시안 shell.jsx NAV 기준 ⊕ 로 통일 — Sidebar 발견 아이콘(⊕)과 동일
  { id: 'explore', label: '발견', icon: '⊕', to: '/explore' },
  { id: 'profile', label: '나', icon: '○', to: '/profile' },
];

export function BottomTabBar(): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // [이유] 정확 매칭 — '/profile' 과 '/settings' 가 동시에 active 로 잡히는 일 방지
  const isActive = (to: string): boolean => pathname === to;

  return (
    <Box
      component="nav"
      aria-label="하단 네비게이션"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        bgcolor: c.paper[0][L],
        borderTop: `1px solid ${BORDER}`,
        display: 'flex',
        pb: 'calc(2px + env(safe-area-inset-bottom))',
        flexShrink: 0,
      }}
    >
      {TABS.map((t) => {
        const active = isActive(t.to);
        return (
          <ButtonBase
            key={t.id}
            onClick={() => navigate(t.to)}
            aria-label={t.label}
            aria-current={active ? 'page' : undefined}
            sx={{
              flex: 1,
              flexDirection: 'column',
              gap: '2px',
              py: '7px',
              color: active ? BRAND : TEXT_LOW,
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: 19, lineHeight: 1, color: active ? BRAND : TEXT_LOW }}
            >
              {t.icon}
            </Box>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: active ? 600 : 400,
                color: active ? BRAND : TEXT_HI,
                lineHeight: 1.2,
              }}
            >
              {t.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
